/**
 * App.tsx — composition root only.
 * Providers + a single NavigationContainer hosting the root navigator.
 * All routing lives under src/navigation/ (rootNavigator → authStack / appShell).
 */

import React from 'react';
import { NavigationContainer, DefaultTheme, DarkTheme, Theme as NavTheme } from '@react-navigation/native';

import ToastHost from './src/components/toastHost';
import { ThemeProvider, useTheme } from './src/context/themeContext';
import { AppProvider } from './src/context/appContext';
import { SessionProvider } from './src/context/sessionContext';
import { ToastProvider } from './src/components/toast';
import GlobalErrorBoundary from './src/components/globalErrorBoundary';
import RootNavigator from './src/navigation/rootNavigator';
import { navigationRef } from './src/navigation/navigationRef';
import { linking } from './src/navigation/linking';

/**
 * NavigationContainer paints every screen's scene background from its own theme
 * (React Navigation's DefaultTheme is light — rgb(242,242,242)). Without this,
 * dark mode leaves the content area light behind the (correctly dark) cards.
 * Sync the navigation theme to our app theme so the scene background follows.
 */
function ThemedNavigation() {
  const { theme, isDark } = useTheme();
  const { colors } = theme;
  const base = isDark ? DarkTheme : DefaultTheme;
  const navTheme: NavTheme = {
    ...base,
    colors: {
      ...base.colors,
      background: colors.paper,
      card: colors.card,
      text: colors.ink,
      border: colors.line,
      primary: colors.accent,
      notification: colors.accent,
    },
  };
  return (
    <NavigationContainer ref={navigationRef} theme={navTheme} linking={linking}>
      <RootNavigator />
    </NavigationContainer>
  );
}

export default function App() {
  return (
    // Outermost: catch any render/runtime crash anywhere in the tree and show
    // the fallback instead of a white screen.
    <GlobalErrorBoundary>
      <ThemeProvider>
        <AppProvider>
          <SessionProvider>
            <ToastProvider>
              <ThemedNavigation />
              {/* Toasts — premium dark config, portaled above modals (web) */}
              <ToastHost />
            </ToastProvider>
          </SessionProvider>
        </AppProvider>
      </ThemeProvider>
    </GlobalErrorBoundary>
  );
}
